import React, { useState, useEffect } from "react";
import "./CheckList.css";
import Graph from "../../../../components/Util/Graph";
import axios from "axios";
import Swal from "sweetalert2";
import * as ChecklistFunction from "../../../../functions/Functions";
import Modal from "./ChecklistModal"; // 가정된 모달 컴포넌트
import { getServerBaseUrl } from "../../../../utils/portConfig";

function CheckList({ patientData }) {
  const [selectedWeek, setSelectedWeek] = useState(12); // 선택된 주차
  const [selectedTab, setSelectedTab] = useState("처방 추천"); // 선택된 탭
  const [patientId, setPatientId] = useState("");
  const [selectedGraph, setSelectedGraph] = useState("bmi"); // 선택된 그래프
  const [patientHistory, setPatientHistory] = useState({}); // 그래프 데이터
  const [testData, setTestData] = useState([]); // 검사 데이터
  const [testStatus, setTestStatus] = useState([]); // 검사 상태
  const [groupedTestData, setGroupedTestData] = useState(null); // 검사 정보 데이터
  const [tagContents, setTagContents] = useState([]);
  const [adviceData, setAdviceData] = useState([]);
  const [selectedAdvice, setSelectedAdvice] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedTests, setSelectedTests] = useState([]);
  const [currentAdvice, setCurrentAdvice] = useState(null);
  const [previousApplicationStatus, setPreviousApplicationStatus] = useState(null);
  const [redLines, setRedLines] = useState([]);

  const [applicationStatus, setApplicationStatus] = useState(null);
  const [recommendStatus, setRecommendStatus] = useState(null);
  const [doNotRecommend, setDoNotRecommend] = useState(null); // 다시는 처방 추천하지 않기

  const API_SERVER_URL = `${getServerBaseUrl()}/api/v1`; // API 서버 URL
  const token = localStorage.getItem("token"); // 토큰
  const id = localStorage.getItem("id");
  const userId = localStorage.getItem("userid");

  useEffect(() => {
    if (patientData) {
      setAdviceData([]);
      setSelectedAdvice(null);
      setTagContents([]);
    }
  }, [patientData]);

  useEffect(() => {
    if (patientData && !patientData.hasOwnProperty("status")) {
      // 환자 데이터를 사용하여 상태 변수 초기화
      const initialTestStatus = {};

      setSelectedTab("처방 추천");
      setSelectedGraph("bmi");

      // patientData가 예상한 속성을 포함하는지 확인한 후 forEach를 사용합니다.
      if (patientData.patientTest && Array.isArray(patientData.patientTest)) {
        patientData.patientTest.forEach((testCategory) => {
          if (testCategory.testDetail && Array.isArray(testCategory.testDetail)) {
            testCategory.testDetail.forEach((testDetail) => {
              if (testDetail.testResult === "N" || testDetail.testResult === "A" || testDetail.testResult === "X") {
                initialTestStatus[testDetail.testId] = testDetail.testResult;
              }
            });
          }
        });
      }

      setTestStatus(initialTestStatus);
      setSelectedWeek(patientData.currentWeek);
      setTestData(patientData.patientTest);
      setPatientHistory(patientData.patientVitalDTO);
      setAdviceData(patientData.advices);

      // testData를 startWeek와 endWeek 기준으로 그룹화하여 groupedTestData에 저장
      const test = patientData.patientTest;
      test.sort((a, b) => {
        // a와 b의 startWeek 및 endWeek가 null이 아닌 경우에만 정렬
        if (a.startWeek !== null && b.startWeek !== null && a.endWeek !== null && b.endWeek !== null) {
          // startWeek를 비교하여 정렬하고, startWeek가 같은 경우 endWeek를 비교하여 오름차순으로 정렬
          if (a.startWeek === b.startWeek) {
            return a.endWeek - b.endWeek;
          }
          return a.startWeek - b.startWeek;
        }
        // startWeek 또는 endWeek가 null인 경우에는 정렬하지 않음
        return 0;
      });
      const groupedData = test.reduce((groups, testCategory) => {
        const weekKey = `${testCategory.startWeek}-${testCategory.endWeek}`;
        if (!groups[weekKey]) {
          groups[weekKey] = [];
        }
        groups[weekKey].push(testCategory);
        return groups;
      }, {});

      setGroupedTestData(groupedData);
    }
  }, [patientData]);

  useEffect(() => {
    if (selectedGraph === "bmi") {
      // bmi 그래프에 대한 빨간 선 데이터 결정
      setRedLines([
        { value: 18.5, color: "red" },
        { value: 23, color: "red" }, // 예시: 체중 그래프에 대한 빨간 선
      ]);
    } else if (selectedGraph === "temperature") {
      // temperature 그래프에 대한 빨간 선 데이터 설정
      setRedLines([
        { value: 36, color: "red" },
        { value: 37.5, color: "red" }, // 예시: 체온 그래프에 대한 빨간 선
      ]);
    } else if (selectedGraph === "bloodPressure") {
      // bloodPressure 그래프에 대한 빨간 선 데이터 설정
      setRedLines([
        { value: 70, color: "red" },
        { value: 140, color: "red" }, // 예시: 최고 혈압에 대한 빨간 선
      ]);
    } else if (selectedGraph === "bloodSugar") {
      // bloodSugar 그래프에 대한 빨간 선 데이터 설정
      setRedLines([
        { value: 70, color: "red" },
        { value: 99, color: "red" }, // 예시: 혈당 그래프에 대한 빨간 선
      ]);
    }
  }, [selectedGraph]);

  useEffect(() => {
    if (patientData && !patientData.hasOwnProperty("status") && patientData.advices.length !== 0) {
      if (adviceData.length > 0) {
        const firstAdvice = adviceData[0];
        setSelectedAdvice(firstAdvice);
        // 첫 번째 advice의 completed 상태에 따라 applicationStatus 설정
        setApplicationStatus(firstAdvice.advice.completed === true ? "적용" : firstAdvice.advice.completed === false ? "미적용" : null);
        setRecommendStatus(firstAdvice.advice.neverRecommend === true ? "NO" : null);

        // 적용 미적용 설정
        const completed = adviceData[0].advice.completed;
        if (completed === true) {
          setApplicationStatus("적용");
        } else if (completed === false) {
          setApplicationStatus("미적용");
        } else {
          setApplicationStatus(null);
        }

        //처방추천 여부 설정
        const neverRecommend = adviceData[0].advice.neverRecommend;
        if (neverRecommend === true) {
          setRecommendStatus("NO");
        } else {
          setRecommendStatus(null);
        }
      }
    }
    // '택 3종'이 포함된 advice 찾기
    // const adviceWithThreeChoice = adviceData.find(advice =>
    //   advice.testMasterCodeList && advice.testMasterCodeList.length >= 2
    // );
    // if (adviceWithThreeChoice) {
    //   setCurrentAdvice(adviceWithThreeChoice);
    // }
  }, [patientData, adviceData]);

  const isTestDisabled = (testItem) => {
    // 검사결과가 있으면서 testWeek가 존재하고, testWeek가 selectedWeek보다 작은 경우에만 비활성화합니다.
    // if(testItem.testResult !== '' && testItem.testResult != null){
    //   return testItem.testWeek !== null && testItem.testWeek < selectedWeek;
    // }
    if (testItem.testDetail.length > 0) {
      for (const detail of testItem.testDetail) {
        const testResult = detail.testResult;
        if (testResult !== "N" && testResult !== "A") {
          return false;
        } else {
          return true;
        }
      }

      // 모든 요소가 'N' 또는 'A'인 경우에만 아래 조건을 검사합니다.
      //return testItem.testWeek !== null && testItem.testWeek < selectedWeek;
    }
  };

  // 선택된 주차 주변의 주차 정보를 생성
  const weeks = [];
  for (let i = selectedWeek - 2; i <= selectedWeek + 2; i++) {
    if (i >= 1 && i <= 42) {
      // 주차는 1부터 42까지 존재
      const isGray = i > selectedWeek; // 선택된 주차 이후인 경우 회색 표시
      weeks.push({ week: i, isGray }); // 주차 정보를 배열에 추가
    }
  }

  // 탭을 클릭했을 때 호출되는 함수
  const handleTabClick = (tabName) => {
    setSelectedTab(tabName); // 선택된 탭을 업데이트
  };

  // 그래프 타입에 따라 내용을 표시하는 함수
  const handleShowContent = async (graphType) => {
    setSelectedGraph(graphType);
  };

  // 검사 상태를 변경하는 함수
  const handleTestStatusChange = (testId, status) => {
    setTestStatus((prevTestStatus) => ({
      ...prevTestStatus,
      [testId]: prevTestStatus[testId] === status ? null : status,
    }));
  };

  // 검사 정보 저장 함수
  const handleTestSave = async () => {
    try {
      // 업데이트할 테스트 결과를 저장할 빈 배열을 생성합니다.
      const updatedTests = [];

      for (const testItem of testData) {
        for (const testDetail of testItem.testDetail) {
          const currentStatus = testStatus[testDetail.testId];
          const originalStatus = testDetail.testResult;

          if (currentStatus !== undefined && currentStatus !== null && currentStatus !== originalStatus) {
            updatedTests.push({
              patientId: testItem.patientId,
              testId: testDetail.testId,
              currentWeek: selectedWeek,
              testResult: currentStatus,
            });
          }
        }
      }
      // 업데이트할 결과가 있다면 서버로 POST 요청을 보냅니다.
      if (updatedTests.length > 0) {
        const response = await axios.post(
          `${API_SERVER_URL}/checklist/${testData[0].patientId}/tests/update/${userId}`, // 서버 API 엔드포인트를 수정해야 할 수 있습니다.
          updatedTests, // 업데이트할 테스트 결과 배열을 본문으로 보냅니다.
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        //console.log('서버에 변경된 테스트 결과가 모두 업데이트되었습니다.');
        Swal.fire({
          icon: "success",
          title: "성공",
          text: "검사 정보가 저장되었습니다.",
          customClass: {
            popup: "custom-swal-popup",
          },
        });
      } else {
        console.error("업데이트할 테스트 결과가 없습니다.");
      }
    } catch (error) {
      console.error("서버에 변경된 테스트 결과를 업데이트하는 데 실패했습니다.", error);
    }
  };

  // tooltip 기능을 가진 cellRenderer 함수
  function cellRenderer(params) {
    return (
      <span className="title-cell" title={params.value}>
        {params.value}
      </span>
    );
  }

  const rightContentStyle = {
    backgroundColor: selectedTab === "검사 정보" ? "#f3f0f0" : "white",
  };

  const tabAreaStyle = {
    backgroundColor: selectedTab === "검사 정보" ? "white" : "white",
  };

  const handleInfoItemClick = (data) => {
    setTagContents(data);
    setSelectedTab("관련 고시"); // 관련 고시 탭으로 이동
  };

  // 다시는 처방추천을 받지 않겠습니다.
  const deleteAdviceNeverRecommendTrue = (aIndex, adviceData) => {
    if (adviceData[aIndex] && adviceData[aIndex].advice.neverRecommend === true) {
      adviceData.splice(aIndex, 1);
    }
    setAdviceData(adviceData);
  };

  // 다시는 추천 안받기
  const handleNeverRecommendSave = async (adviceId, noRecommend) => {
    setDoNotRecommend(noRecommend);
    const confirmationResult = await Swal.fire({
      icon: "question",
      title: "처방 추천 안함",
      text: "해당 처방을 더는 추천받지 않으시겠습니까? 이 버튼이 활성화 되면 다음 진료부터 해당 처방을 추천하지 않습니다.",
      showCancelButton: true,
      confirmButtonText: "확인",
      cancelButtonText: "취소",
      customClass: {
        popup: "custom-swal-popup-neverRecommend",
      },
    });

    if (confirmationResult.isConfirmed) {
      const url = `${API_SERVER_URL}/checklist/${adviceId}/updateRecommendStatus/${id}?neverRecommend=${noRecommend}`;
      const advice = adviceData.find((a) => a.advice.id === adviceId);
      const adviceIndex = adviceData.findIndex((a) => a.advice.id === adviceId);

      if (advice) {
        setSelectedAdvice(advice);
        setCurrentAdvice(advice);
        try {
          const response = await axios.put(
            url,
            {},
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          if (response.status === 200) {
            adviceData[adviceIndex].advice.neverRecommend = noRecommend;
            setAdviceData([...adviceData]);
            setCurrentAdvice(advice);
            Swal.fire({
              icon: "success",
              title: "성공",
              text: "다음 진료부터 해당 환자에게 이 처방을 추천하지 않습니다.",
              customClass: {
                popup: "custom-swal-popup",
              },
            }).then(() => {
              deleteAdviceNeverRecommendTrue(adviceIndex, adviceData);
            });
          }
        } catch (error) {
          console.error("Error updating data:", error);
          Swal.fire({
            icon: "error",
            title: "오류",
            text: "처방 추천 여부 업데이트 실패",
            customClass: {
              popup: "custom-swal-popup",
            },
          });
        }
      }
    } else {
      setRecommendStatus(null);
    }
  };

  const handleSaveClick = async (adviceId, isApplied) => {
    setApplicationStatus(isApplied);
    const url = `${API_SERVER_URL}/checklist/${adviceId}/update/${id}?completed=${isApplied}`;
    const advice = adviceData.find((a) => a.advice.id === adviceId);
    const adviceIndex = adviceData.findIndex((a) => a.advice.id === adviceId);
    if (advice) {
      setSelectedAdvice(advice);

      // if (advice && advice.testMasterCodeList != null && isApplied) {
      // testMasterCodeList의 개수가 2개 이상일 때 모달 표시

      //setSelectedTests([]);

      // } else {
      // 그렇지 않은 경우 상태를 바로 업데이트합니다.

      setCurrentAdvice(advice);
      setPreviousApplicationStatus(applicationStatus);
      // setShowModal(true);

      try {
        const response = await axios.put(
          url,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.status === 200) {
          adviceData[adviceIndex].advice.completed = isApplied;
          setAdviceData(adviceData);
          setCurrentAdvice(advice);
          setPreviousApplicationStatus(applicationStatus);

          //변경할 데이터
          Swal.fire({
            icon: "success",
            title: "성공",
            text: "처방 추천 적용 상태가 업데이트되었습니다.",
            customClass: {
              popup: "custom-swal-popup",
            },
          });

          if (isApplied === true && advice.testMasterCodeList != null && advice.testMasterCodeList.length > 0) {
            setShowModal(true);
          }
        }
      } catch (error) {
        console.error("Error updating data:", error);
        Swal.fire({
          icon: "error",
          title: "오류",
          text: "처방 추천 적용 업데이트 실패",
          customClass: {
            popup: "custom-swal-popup",
          },
        });
      }
      // }
    }

    if (isApplied) {
      setApplicationStatus("적용");
    } else {
      setApplicationStatus("미적용");
    }
  };

  const handleSelectAdvice = (data) => {
    setSelectedAdvice(data);
    // 'applicationStatus' 상태를 'advice'의 'completed' 값에 따라 업데이트
    setApplicationStatus(data.advice.completed === true ? "적용" : data.advice.completed === false ? "미적용" : null);
    setRecommendStatus(data.advice.neverRecommend === true ? "NO" : null);
  };

  // handleCheckboxChange 함수 수정
  const handleCheckboxChange = (testId) => {
    const selectedIndex = selectedTests.indexOf(testId);
    const isAlreadySelected = selectedIndex !== -1;
    const maxSelection = selectedAdvice && selectedAdvice.advice.adviceName.includes("택 3종") ? 3 : 1; // 택 3종인 경우 최대 3개 선택 가능

    if (isAlreadySelected) {
      // 이미 선택된 경우 선택 해제
      setSelectedTests(selectedTests.filter((id) => id !== testId));
    } else if (selectedTests.length < maxSelection) {
      // 선택된 항목이 최대 선택 가능 개수 미만인 경우 새로운 항목 추가
      setSelectedTests([...selectedTests, testId]);
    } else {
      // 최대 선택 가능 개수를 넘은 경우 추가하지 않음 (또는 사용자에게 경고 표시)
      // 예: Swal 사용하여 사용자에게 알림
      Swal.fire({
        icon: "warning",
        title: "주의",
        text: `최대 ${maxSelection}개까지만 선택 가능합니다.`,
        customClass: {
          popup: "custom-swal-popup",
        },
      });
    }
  };

  const handleSave = () => {
    // 여기에 선택된 항목 저장 로직 구현
    setShowModal(false);
  };

  const closeModal = () => {
    setShowModal(false);
    // 선택된 처방 추천의 상태를 초기화합니다.
    //setApplicationStatus(previousApplicationStatus); // 또는 적절한 초기 상태로 설정
  };

  return (
    <div className="right-content" style={rightContentStyle}>
      <div className="right-group">
        <div className="tab-area" style={tabAreaStyle}>
          <div className={`tab ${selectedTab === "처방 추천" ? "selected-tab" : ""}`} onClick={() => handleTabClick("처방 추천")}>
            처방 추천
          </div>
          <div className={`tab ${selectedTab === "관련 고시" ? "selected-tab" : ""}`} onClick={() => handleTabClick("관련 고시")}>
            관련 고시
          </div>
          <div className={`tab ${selectedTab === "검사 정보" ? "selected-tab" : ""}`} onClick={() => handleTabClick("검사 정보")}>
            검사 정보
          </div>
          <div className={`tab ${selectedTab === "VITAL" ? "selected-tab" : ""}`} onClick={() => handleTabClick("VITAL")}>
            VITAL
          </div>
          {/* <div
            className={`tab ${selectedTab === '미처리이벤트' ? 'selected-tab' : ''}`}
            onClick={() => handleTabClick('미처리이벤트')}
          >
            미처리이벤트
          </div> */}
        </div>
        {selectedTab === "처방 추천" && (
          <div style={{ height: "100%", backgroundColor: "#f3f0f0" }}>
            <div className="checklist-container">
              {selectedAdvice ? (
                <div className="test-content">
                  <div className="test-title">{selectedAdvice.advice.adviceName}</div>
                  <div className="test-details">
                    <div className="test-evidence">근거: {selectedAdvice.advice.reason}</div>
                    <div className="test-related-notice">
                      관련고시:{" "}
                      <span className="gosi-title" onClick={() => handleInfoItemClick(selectedAdvice.gosiDetailDTO)}>
                        {selectedAdvice.gosiDetailDTO.title}
                      </span>
                    </div>
                  </div>
                  <div className="test-content-actions">
                    <label>
                      <input
                        type="radio"
                        name="applicationStatus"
                        value="적용"
                        checked={applicationStatus === "적용"}
                        onChange={() => {
                          setApplicationStatus("적용");
                          handleSaveClick(selectedAdvice.advice.id, true);
                        }}
                      />
                      적용
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="applicationStatus"
                        value="미적용"
                        checked={applicationStatus === "미적용"}
                        onChange={() => {
                          setApplicationStatus("미적용");
                          handleSaveClick(selectedAdvice.advice.id, false);
                        }}
                      />
                      미적용
                    </label>
                  </div>
                  <div className="never-recommend">
                    <label>
                      <input
                        type="checkbox"
                        id={`${selectedAdvice.advice.id}`}
                        name={`neverRecommend_${selectedAdvice.advice.id}`}
                        value="NO"
                        checked={recommendStatus === "NO"}
                        onChange={() => {
                          handleNeverRecommendSave(selectedAdvice.advice.id, true);
                          setRecommendStatus("NO");
                        }}
                      />
                      다시는 처방추천을 받지 않겠습니다.
                    </label>
                  </div>
                </div>
              ) : (
                <div className="gosiWrapper-guide">
                  <p>추천할 처방이 없습니다.</p>
                </div>
              )}
              <div className="advice-selector">
                {adviceData.map((advice, index) => (
                  <button key={index} className={`advice-button ${selectedAdvice === advice ? "active" : ""}`} onClick={() => handleSelectAdvice(advice)}>
                    처방추천{index + 1}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        {selectedTab === "관련 고시" && patientData && !patientData.hasOwnProperty("status") && (
          <div>
            <div className="wrapper gosiWrapper">
              {tagContents && tagContents.title ? (
                <>
                  <div className="gosiWrapper-title">{tagContents.title}</div>
                  <div dangerouslySetInnerHTML={{ __html: tagContents.tagcontents }} className="gosiWrapper-content"></div>
                </>
              ) : (
                <div className="gosiWrapper-guide">
                  <p>고시 정보를 보려면, '처방 추천' 탭에서 관련 고시 항목을 선택해주세요.</p>
                </div>
              )}
            </div>
          </div>
        )}
        {selectedTab === "검사 정보" && patientData && !patientData.hasOwnProperty("status") && (
          <div>
            <div className="scrollable-area">
              {groupedTestData &&
                Object.keys(groupedTestData).map((weekKey, weekIndex) => (
                  <div className="checklist-item-group" key={weekIndex}>
                    {!weekKey.includes(null) ? <div className="checklist-item-date">임신 {weekKey.replace("-", " ~ ")}주차</div> : <div className="checklist-item-date">추가 검사</div>}
                    {groupedTestData[weekKey].map((testItem, index) => (
                      <div className="checklist-item" key={index}>
                        <div className="checklist-item-name align-left">
                          <div className="test-category">검사</div>
                          {testItem.testName}
                          {/* 상세 검사 내역 표시 */}
                          {testItem.testDetail.map((detailItem, detailIndex) => (
                            <div key={detailIndex} className="checklist-item-detail">
                              <div className="checklist-item-testname">{detailItem.testName}</div>
                              {/* 여기에 상세 검사 내역의 결과 입력 컴포넌트 추가 */}
                              <div className="checklist-item-result">
                                <label>
                                  <input
                                    type="radio"
                                    name={`testResult-${detailItem.testId}`}
                                    value="normal"
                                    checked={testStatus[detailItem.testId] === "N"}
                                    onChange={() => handleTestStatusChange(detailItem.testId, "N")}
                                  />
                                  정상
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    name={`testResult-${detailItem.testId}`}
                                    value="abnormal"
                                    checked={testStatus[detailItem.testId] === "A"}
                                    onChange={() => handleTestStatusChange(detailItem.testId, "A")}
                                  />
                                  비정상
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    name={`testResult-${detailItem.testId}`}
                                    value="X"
                                    checked={testStatus[detailItem.testId] === "X"}
                                    onChange={() => handleTestStatusChange(detailItem.testId, "X")}
                                  />
                                  미시행
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
            </div>
            <div className="testButton-container">
              <button onClick={handleTestSave}>저장</button>
            </div>
          </div>
        )}
        {selectedTab === "VITAL" && Object.keys(patientHistory).length !== 0 && !patientData.hasOwnProperty("status") && (
          <div style={{ height: "calc(100vh - 196px)", paddingTop: "10px", overflowY: "auto" }}>
            {selectedGraph === "bmi" && <Graph data={patientHistory.bmi} category={patientHistory.categories} tooltipFormatter={(value) => `BMI ${value}`} redLines={redLines} />}
            {selectedGraph === "temperature" && <Graph data={patientHistory.temperatureList} category={patientHistory.categories} tooltipFormatter={(value) => `${value} °C`} redLines={redLines} />}
            {selectedGraph === "bloodPressure" && redLines.length === 2 && (
              <Graph
                data={[patientHistory.minBloodPressureList, patientHistory.maxBloodPressureList]}
                category={patientHistory.categories}
                tooltipFormatter={(value) => `${value} mmHg`}
                lineColors={["#FF5733", "#33FF57"]} // 각 선의 색상을 지정
                redLines={redLines}
              />
            )}
            {selectedGraph === "bloodSugar" && redLines.length === 2 && (
              <Graph data={patientHistory.bloodGlucoseList} category={patientHistory.categories} tooltipFormatter={(value) => `${value} mg/dL`} redLines={redLines} />
            )}
            <div className="checklist-button-container">
              <button className={`button weight-button ${selectedGraph === "bmi" ? "active" : ""}`} onClick={() => handleShowContent("bmi")}>
                bmi변화
              </button>
              <button className={`button temperature-button ${selectedGraph === "temperature" ? "active" : ""}`} onClick={() => handleShowContent("temperature")}>
                체온변화
              </button>
              <button className={`button bloodPressure-button ${selectedGraph === "bloodPressure" ? "active" : ""}`} onClick={() => handleShowContent("bloodPressure")}>
                혈압변화
              </button>
              <button className={`button bloodSugar-button ${selectedGraph === "bloodSugar" ? "active" : ""}`} onClick={() => handleShowContent("bloodSugar")}>
                혈당변화
              </button>
            </div>
          </div>
        )}
        {/* {selectedTab === '미처리이벤트' && (
          <div className="wrapper">
            <div className="custom-table2">
              <div className="row header3">
                <div className="cell">미처리이벤트</div>
                <div className="cell">구분</div>
              </div> */}

        {/* 미처리된 처방 추천 정보 표시 */}
        {/* {adviceData.filter(advice => advice.advice.completed === null).map(advice => (
                <div className="row" key={advice.advice.id}>
                  <div className="cell align-left">
                    {cellRenderer({ value: advice.advice.adviceName })}
                  </div>
                  <div className="cell">처방 추천</div>
                </div>
              ))} */}

        {/* 미처리된 검사 정보 표시 */}
        {/* {testData.flatMap((testItem) => {
                //const isTestUnprocessed = testItem.testResult === '' || testItem.testResult === null || testItem.testResult === undefined;
                if (!isTestDisabled(testItem)) { //isTestUnprocessed && 
                  return (
                    <div className="row" key={testItem.testId}>
                      <div className="cell align-left">
                        {cellRenderer({ value: testItem.testName })}
                      </div>
                      <div className="cell">검사 정보</div>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        )} */}
      </div>
      {showModal && currentAdvice && (
        <Modal
          testList={currentAdvice.testMasterCodeList || []}
          selectedTests={selectedTests}
          onCheckboxChange={handleCheckboxChange}
          //onSave={handleSave}
          onClose={closeModal}
        />
      )}
    </div>
  );
}

export default CheckList;
